import { create } from 'zustand';
import { changePassphrase, createVault, unlockVault, type VaultKey } from '@/modules/storage/crypto';
import { destroyProfileDatabase } from '@/modules/storage/indexedDbStore';
import { openProfileStorage, type ProfileStorage } from '@/modules/storage/profileStorage';
import {
  forgetVaultRecord,
  loadVaultRecord,
  saveVaultRecord,
} from '@/modules/storage/vaultStorage';
import type { ProfileId } from '@/modules/storage/entities';

/**
 * Which profile the application is in, and whether the private one is open.
 *
 * The store holds at most one private `ProfileStorage` at a time and drops it
 * on lock. Because the vault key lives only inside that object, locking makes
 * the private data genuinely unreadable again rather than merely hidden: there
 * is no key left in memory to read it with.
 */

export type PrivateState =
  /** No vault has ever been created on this machine. */
  | { readonly status: 'absent' }
  /** A vault exists but is closed. */
  | { readonly status: 'locked' }
  | { readonly status: 'unlocking' }
  | { readonly status: 'unlocked'; readonly storage: ProfileStorage }
  | { readonly status: 'error'; readonly message: string };

interface ProfileStore {
  readonly activeProfile: ProfileId;
  readonly shared: ProfileStorage | null;
  readonly privateState: PrivateState;

  initialise: () => Promise<void>;
  createPrivateVault: (passphrase: string) => Promise<boolean>;
  unlockPrivate: (passphrase: string) => Promise<boolean>;
  lockPrivate: () => void;
  changePrivatePassphrase: (current: string, next: string) => Promise<boolean>;
  /** Permanently destroy the private profile and everything in it. */
  erasePrivate: () => Promise<void>;
  switchProfile: (profile: ProfileId) => void;
}

export const useProfileStore = create<ProfileStore>((set, get) => ({
  activeProfile: 'shared',
  shared: null,
  privateState: { status: 'absent' },

  initialise: async () => {
    const shared = await openProfileStorage('shared');
    set({
      shared,
      privateState: loadVaultRecord() === null ? { status: 'absent' } : { status: 'locked' },
    });
  },

  createPrivateVault: async (passphrase) => {
    if (passphrase.length < 8) {
      set({ privateState: { status: 'error', message: 'Use at least 8 characters.' } });
      return false;
    }

    set({ privateState: { status: 'unlocking' } });
    try {
      const { record, key } = await createVault(passphrase);
      saveVaultRecord(record);
      const storage = await openProfileStorage('private', key);
      set({ privateState: { status: 'unlocked', storage }, activeProfile: 'private' });
      return true;
    } catch {
      set({ privateState: { status: 'error', message: 'Could not create the private space.' } });
      return false;
    }
  },

  unlockPrivate: async (passphrase) => {
    const record = loadVaultRecord();
    if (record === null) {
      set({ privateState: { status: 'absent' } });
      return false;
    }

    set({ privateState: { status: 'unlocking' } });
    // Key derivation is deliberately slow, so this genuinely takes a moment.
    const key: VaultKey | null = await unlockVault(passphrase, record);
    if (key === null) {
      set({ privateState: { status: 'error', message: 'That passphrase is not right.' } });
      return false;
    }

    const storage = await openProfileStorage('private', key);
    set({ privateState: { status: 'unlocked', storage }, activeProfile: 'private' });
    return true;
  },

  lockPrivate: () => {
    const state = get().privateState;
    if (state.status === 'unlocked') state.storage.close();
    // Dropping the reference drops the only copy of the key.
    set({ privateState: { status: 'locked' }, activeProfile: 'shared' });
  },

  changePrivatePassphrase: async (current, next) => {
    const record = loadVaultRecord();
    if (record === null || next.length < 8) return false;

    const updated = await changePassphrase(current, next, record);
    if (updated === null) return false;

    // Only the wrapped key changes. No stored record is touched.
    saveVaultRecord(updated);
    return true;
  },

  erasePrivate: async () => {
    const state = get().privateState;
    if (state.status === 'unlocked') state.storage.close();

    await destroyProfileDatabase('private');
    forgetVaultRecord();
    set({ privateState: { status: 'absent' }, activeProfile: 'shared' });
  },

  switchProfile: (profile) => {
    // Refuse to enter a profile that is not open. Without this the interface
    // could show a private screen backed by no storage at all.
    if (profile === 'private' && get().privateState.status !== 'unlocked') return;
    set({ activeProfile: profile });
  },
}));

/**
 * The storage for whichever profile is active.
 *
 * Every screen reads its data through this, which is what makes the isolation
 * automatic: a component asks for "the current profile's history" and cannot
 * name a profile even if it wanted to.
 */
export function useActiveStorage(): ProfileStorage | null {
  return useProfileStore((state) => {
    if (state.activeProfile === 'private') {
      return state.privateState.status === 'unlocked' ? state.privateState.storage : null;
    }
    return state.shared;
  });
}
